import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuditTrailData } from "../util/getAuditTrailData";
import { getRevAndTraining, uploadJsonToS3, adjustSiblingOrderAndInsert, prepareCurrentSnapshot } from "../util/AppUtils";
import { bomRowsMapper, StepDetailsMapper } from "../pages/Documents/Utils/utils";
import { v4 as uuidv4 } from 'uuid';
import { updateCurrentSnapshot } from "./procedureRevisionSlice";

// Base URLs
const baseUrl = `${BASE_URL}step-tree-node`;
const richTextBaseUrl = `${BASE_URL}rich-text-tree`;
const canvasBaseUrl = `${BASE_URL}canvas-tree`;
const videoBaseUrl = `${BASE_URL}video-tree-node`;
const attachmentBaseUrl = `${BASE_URL}attachment-tree`;
const substepBaseUrl = `${BASE_URL}sub-step-tree-node`;
const formBaseUrl = `${BASE_URL}form-tree`;
const nodeBaseUrl = `${BASE_URL}`;

/* ──────────────── CRUD AsyncThunks ──────────────── */

// Create Step
export const createStep = createAsyncThunk(
  "step/create",
  async ({ stepData, comment, version_content,allNodesData,partnumber,revision }, { dispatch, rejectWithValue }) => {
    
    const tree_node_id = uuidv4();
    // const tree_node_id = "";

    const ad = await getAuditTrailData({
      action: "CREATE_STEP",
      comment: comment,
      version_content:{
        ...version_content,
        tree_node_id
      },
    });

    
    const {item_code_type,status} = JSON.parse(JSON.stringify(version_content))
    
    const newStep = {
      tree_node_id: tree_node_id,
      section_id_ref:stepData.section_id_ref,
      substeps:[],
      children:[],
      sibling_order:stepData.sibling_order,
      procedure_tree:{sibling_order:stepData.sibling_order},
      created_at:new Date().toISOString(),
      is_deleted:false,
      detail:{
        ...stepData,
        step_data:stepData.step_data_prepared
      },
      isDraft:true,
    };
    delete stepData.step_data_prepared;
    const snapshotPayload = JSON.parse(JSON.stringify(allNodesData))
    snapshotPayload.forEach((section) => {
      if (section.section_id_ref === stepData.section_id_ref) {
        adjustSiblingOrderAndInsert(section.steps, newStep);
      }
    });
    
    const previous_content_snapshot = await uploadJsonToS3(allNodesData,partnumber,revision+"_step_previous",true)

    const revAndTrainingData = await getRevAndTraining(dispatch, stepData.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3({
      data:snapshotPayload,
      ...ad.version_content,
      operation: "single_audit_trail",
      ...revAndTrainingData,
    },partnumber,revision+"_step_new",true)

    
    const payload = { 
      ...stepData,
      audit_trail_data: {
        ...ad,
        previous_content_snapshot,
        new_content_snapshot,
        version_content:{
          action_to_show:ad.version_content.action_to_show,
        }
      }, 
    };
    try {
      const response = await axios.post(`${baseUrl}/create`, payload);
      
      await dispatch(
        updateCurrentSnapshot({
          partId: stepData.part_required_id_ref,
          revisionId: stepData.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating step");
    }
  }
);

// Update Step
export const updateStep = createAsyncThunk(
  "step/update",
  async ({ id, data, allNodesData, partnumber, revision, tree_node_id}, { dispatch, rejectWithValue }) => {

    const {item_code_type,status} = JSON.parse(JSON.stringify(data.audit_trail_data.version_content))

    const snapshotPayload = JSON.parse(JSON.stringify(allNodesData))

    snapshotPayload.forEach(section=>{
      if(section.section_id_ref === data.section_id_ref){
        section.steps.forEach((step,index)=>{
          if(step.tree_node_id === id){
            section.steps[index] = {
              ...step,
              updated_at:new Date().toISOString(),
              detail:{
                ...step.detail,
                short_description:data.short_description,
                ...data,
                step_data:data.step_data_prepared
              }
            }
          }
        })
      }
    })
    delete data.step_data_prepared;
    const previous_content_snapshot = await uploadJsonToS3(allNodesData,partnumber,revision+"_step_previous",true)

    const revAndTrainingData = await getRevAndTraining(dispatch, data.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3({
        data:snapshotPayload,
        ...data.audit_trail_data.version_content,
        tree_node_id,
        operation: "single_audit_trail",
        ...revAndTrainingData,
      },partnumber,revision+"_step_new",true)

    const payload = { 
      ...data,
      audit_trail_data: {
        ...data.audit_trail_data,
        previous_content_snapshot,
        new_content_snapshot,
        version_content:{
          action_to_show:data.audit_trail_data.version_content.action_to_show,
        }
      },
    };

    try {
      const response = await axios.put(`${baseUrl}/update/${id}`, payload);
      
      await dispatch(
        updateCurrentSnapshot({
          partId: data.part_required_id_ref,
          revisionId: data.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating step");
    }
  }
);

// Delete Step
export const deleteStep = createAsyncThunk(
  "step/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting step");
    }
  }
);

/** BOM “Adjust Qty” — updates quantity for a parts_required row (e.g. total build qty). */
export const updatePartsRequiredQty = createAsyncThunk(
  "step/updatePartsRequiredQty",
  async (
    {
      parts_required_id,
      qty,
      audit_trail_data: auditTrailOverride,
    },
    { rejectWithValue },
  ) => {
    try {
      const hasMeaningfulAudit =
        auditTrailOverride != null &&
        typeof auditTrailOverride === "object" &&
        !Array.isArray(auditTrailOverride) &&
        Object.keys(auditTrailOverride).length > 0;

      const audit_trail_data = hasMeaningfulAudit
        ? auditTrailOverride
        : await getAuditTrailData({
            action: "ADJUST_PARTS_REQUIRED_QTY",
            comment: "Adjust quantity from document BOM",
            version_content: {
              action_to_show: "Adjust Qty",
            },
          });

      const response = await axios.post(
        `${baseUrl}/update-qty`,
        {
          parts_required_id,
          qty,
          audit_trail_data,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      );
      return response.data;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (typeof error.response?.data === "string"
          ? error.response.data
          : null) ||
        error.message ||
        "Error updating quantity";
      return rejectWithValue(msg);
    }
  },
);

// Create Rich Text
export const createRichText = createAsyncThunk(
  "step/richTextCreate",
  async (
    {
      richtextData,
      comment,
      version_content,
      allNodesData,
      partnumber,
      revision,
    },
    { dispatch, rejectWithValue }
  ) => {
    const tree_node_id = uuidv4();
    const ad = await getAuditTrailData({
      action: "CREATE_RICH_TEXT",
      comment: comment,
      version_content: {
        ...version_content,
        tree_node_id,
      },
    });

    const { item_code_type, status } = JSON.parse(JSON.stringify(version_content));

    const newRichTextNode = {
      tree_node_id: tree_node_id,
      type: "rich-text",
      sibling_order: richtextData.sibling_order,
      section_id_ref: richtextData.section_id_ref,
      step_id_ref: richtextData.step_id_ref,
      substep_id_ref: richtextData.substep_id_ref || null,
      created_at: new Date().toISOString(),
      is_deleted: false,
      detail: {
        ...richtextData,
        rich_text_node_id: `rt_ref_${Date.now()}`,
      },
      isDraft: true,
    };
    delete richtextData.category_info;
    const snapshotPayload = JSON.parse(JSON.stringify(allNodesData));

    snapshotPayload.forEach((section) => {
      if (
        section.section_id_ref === richtextData.section_id_ref ||
        section.tree_node_id === richtextData.section_id_ref
      ) {
        if (section.steps) {
          section.steps.forEach((step) => {
            if (
              step.step_id_ref === richtextData.step_id_ref ||
              step.tree_node_id === richtextData.step_id_ref
            ) {
              let targetChildren;
              if (richtextData.substep_id_ref) {
                if (step.substeps) {
                  const substep = step.substeps.find(
                    (ss) =>
                      ss.substep_id_ref === richtextData.substep_id_ref ||
                      ss.tree_node_id === richtextData.substep_id_ref
                  );
                  if (substep) {
                    targetChildren = substep.children;
                    if (!targetChildren) {
                      substep.children = [];
                      targetChildren = substep.children;
                    }
                    adjustSiblingOrderAndInsert(targetChildren, newRichTextNode);
                  }
                }
              } else {
                targetChildren = step.children;
                if (!targetChildren) {
                  step.children = [];
                  targetChildren = step.children;
                }
                adjustSiblingOrderAndInsert(targetChildren, newRichTextNode);
              }
            }
          });
        }
      }
    });

    const previous_content_snapshot = await uploadJsonToS3(
      allNodesData,
      partnumber,
      revision + "_richtext_previous",
      true
    );

    const revAndTrainingData = await getRevAndTraining(dispatch, richtextData.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3({
      data: snapshotPayload,
      ...ad.version_content,
      tree_node_id,
      operation: "single_audit_trail",
      ...revAndTrainingData,
    }, partnumber, revision + "_richtext_new", true);

    const payload = {
      ...richtextData,
      audit_trail_data: {
        ...ad,
        previous_content_snapshot,
        new_content_snapshot,
        version_content: {
          ...ad.version_content,
          action_to_show: ad.version_content.action_to_show,
        },
      },
    };
    try {
      const response = await axios.post(`${richTextBaseUrl}/create`, payload);
      
      await dispatch(
        updateCurrentSnapshot({
          partId: richtextData.part_required_id_ref,
          revisionId: richtextData.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error creating rich text"
      );
    }
  }
);

// Update Rich Text
export const updateRichText = createAsyncThunk(
  "step/richTextCreate", // Note: The action type string was "step/richTextCreate" in the original file, keeping it consistent though "step/richTextUpdate" would be better naming
  async (
    { data, id, allNodesData, partnumber, revision },
    { dispatch, rejectWithValue }
  ) => {
    const {item_code_type,status} = JSON.parse(JSON.stringify(data.audit_trail_data.version_content));
    let tree_node_id = "";

    const snapshotPayload = JSON.parse(JSON.stringify(allNodesData));

    // Find and update the node in snapshotPayload
    snapshotPayload.forEach((section) => {
      if (
        section.section_id_ref === data.section_id_ref ||
        section.tree_node_id === data.section_id_ref
      ) {
        if (section.steps) {
          section.steps.forEach((step) => {
            if (
              step.step_id_ref === data.step_id_ref ||
              step.tree_node_id === data.step_id_ref
            ) {
              let targetChildren;
              if (data.substep_id_ref) {
                if (step.substeps) {
                  const substep = step.substeps.find(
                    (ss) =>
                      ss.substep_id_ref === data.substep_id_ref ||
                      ss.tree_node_id === data.substep_id_ref
                  );
                  if (substep) {
                    targetChildren = substep.children;
                  }
                }
              } else {
                targetChildren = step.children;
              }

              if (targetChildren) {
                const nodeIndex = targetChildren.findIndex(
                  (child) =>
                    child.tree_node_id === id ||
                    child.detail?.rich_text_node_id === id
                );

                if (nodeIndex !== -1) {
                  const existingNode = targetChildren[nodeIndex];
                  tree_node_id = existingNode.tree_node_id;
                  existingNode.detail = {
                    ...existingNode.detail,
                    short_description: data.short_description,
                    rich_text_content: data.rich_text_content, // Ensure content update
                    rich_text_category_ref:
                      data.category_info?.category_id || null, // Ensure category update
                    category_info: data.category_info
                  };
                  existingNode.sibling_order = data.sibling_order;
                }
              }
            }
          });
        }
      }
    });
    delete data.category_info;
    const previous_content_snapshot = await uploadJsonToS3(
      allNodesData,
      partnumber,
      revision + "_richtext_previous",
      true
    );

    const revAndTrainingData = await getRevAndTraining(dispatch, data.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3({
      data: snapshotPayload,
      ...data.audit_trail_data.version_content,
      tree_node_id,
      operation: "single_audit_trail",
      ...revAndTrainingData,
    }, partnumber, revision + "_richtext_new", true);

    const payload = {
      ...data,
      audit_trail_data: {
        ...data.audit_trail_data,
        previous_content_snapshot,
        new_content_snapshot,
        version_content: {
          ...data.audit_trail_data.version_content,
          action_to_show: data.audit_trail_data.version_content.action_to_show,
        },
      },
    };

    try {
      const response = await axios.put(`${richTextBaseUrl}/update/${id}`, payload); // Using 'payload' instead of 'data' to include snapshots
      
      await dispatch(
        updateCurrentSnapshot({
          partId: data.part_required_id_ref,
          revisionId: data.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error creating rich text"
      );
    }
  }
);

// Create Canvas
export const createCanvas = createAsyncThunk(
  "step/canvasCreate",
  async (
    {
      canvasdt,
      comment,
      version_content,
      allNodesData,
      partnumber,
      revision,
    },
    { dispatch, rejectWithValue }
  ) => {
    try {

      const tree_node_id = uuidv4();
      const { item_code_type, status } = JSON.parse(JSON.stringify(version_content));
      const ad = await getAuditTrailData({
        action: "CREATE_CANVAS",
        comment: comment,
        version_content: {
          ...version_content,
          tree_node_id: tree_node_id,
        },
      });

      const newCanvasNode = {
        tree_node_id: tree_node_id,
        type: "canva",
        sibling_order: canvasdt.sibling_order,
        section_id_ref: canvasdt.section_id_ref,
        step_id_ref: canvasdt.step_id_ref,
        substep_id_ref: canvasdt.substep_id_ref || null,
        created_at: new Date().toISOString(),
        is_deleted: false,
        detail: {
          name: canvasdt.name,
          short_description: canvasdt.short_description,
          ctm_data: canvasdt.pages.map((page)=>{
            return {
              canva_snap_url: page?.canva_snap_url || "",
              canvas_tree_node_id_ref: `cnv_ref_${Date.now()}`,
            }
          }),
          canvas_tree_node_id: `cnv_ref_${Date.now()}`,
          canvas_json_data_file: canvasdt.canvas_json_data_file,
        },
        isDraft: true,
      };

      const snapshotPayload = JSON.parse(JSON.stringify(allNodesData));

      snapshotPayload.forEach((section) => {
        if (
          section.section_id_ref === canvasdt.section_id_ref ||
          section.tree_node_id === canvasdt.section_id_ref
        ) {
          if (section.steps) {
            section.steps.forEach((step) => {
              if (
                step.step_id_ref === canvasdt.step_id_ref ||
                step.tree_node_id === canvasdt.step_id_ref
              ) {
                let targetChildren;
                if (canvasdt.substep_id_ref) {
                  if (step.substeps) {
                    const substep = step.substeps.find(
                      (ss) =>
                        ss.substep_id_ref === canvasdt.substep_id_ref ||
                        ss.tree_node_id === canvasdt.substep_id_ref
                    );
                    if (substep) {
                      targetChildren = substep.children;
                      if (!targetChildren) {
                        substep.children = [];
                        targetChildren = substep.children;
                      }
                      adjustSiblingOrderAndInsert(targetChildren, newCanvasNode);
                    }
                  }
                } else {
                  targetChildren = step.children;
                  if (!targetChildren) {
                    step.children = [];
                    targetChildren = step.children;
                  }
                  adjustSiblingOrderAndInsert(targetChildren, newCanvasNode);
                }
              }
            });
          }
        }
      });

      const previous_content_snapshot = await uploadJsonToS3(
        allNodesData,
        partnumber,
        revision + "_canvas_previous",
        true
      );

      const revAndTrainingData = await getRevAndTraining(dispatch, canvasdt.part_required_id_ref)

      const new_content_snapshot = await uploadJsonToS3({
        data: snapshotPayload,
        ...ad.version_content,
        operation: "single_audit_trail",
        ...revAndTrainingData,
      }, partnumber, revision + "_canvas_new", true);

      // Create the proper payload structure
      const payload = {
        name: canvasdt.name,
        pages: canvasdt.pages,
        short_description: canvasdt.short_description,
        sibling_order: canvasdt.sibling_order,
        layout_config: canvasdt.layout_config,
        location_markers: canvasdt.location_markers,
        text_box_properties: canvasdt.text_box_properties,
        text_box_content_data: canvasdt.text_box_content_data,
        shape_data: canvasdt.shape_data,
        image_data: canvasdt.image_data,
        revision_id_ref: canvasdt.revision_id_ref,
        section_id_ref: canvasdt.section_id_ref,
        part_required_id_ref: canvasdt.part_required_id_ref,
        substep_id_ref: canvasdt.substep_id_ref,
        step_id_ref: canvasdt.step_id_ref,
        canvas_json_data_file: canvasdt.canvas_json_data_file,
        audit_trail_data: {
          ...ad,
          previous_content_snapshot,
          new_content_snapshot,
          version_content: {
            action_to_show: ad.version_content.action_to_show,
          }
        },
      };

      const response = await axios.post(`${canvasBaseUrl}/create`, payload);
      
      await dispatch(
        updateCurrentSnapshot({
          partId: canvasdt.part_required_id_ref,
          revisionId: canvasdt.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;

    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating canvas");
    }
  }
);

// Update Canvas
export const updateCanvas = createAsyncThunk(
  "step/canvasCreate",
  async (
    { data, id, allNodesData, partnumber, revision },
    { dispatch, rejectWithValue }
  ) => {
    const {item_code_type,status} = JSON.parse(JSON.stringify(data.audit_trail_data.version_content))
    let tree_node_id = "";
    
    try {
      const snapshotPayload = JSON.parse(JSON.stringify(allNodesData));
      let node_sibling_order = null;
      // Update the node in snapshotPayload
      snapshotPayload.forEach((section) => {
        if (
          section.section_id_ref === data.section_id_ref ||
          section.tree_node_id === data.section_id_ref
        ) {
          if (section.steps) {
            section.steps.forEach((step) => {
              if (
                step.step_id_ref === data.step_id_ref ||
                step.tree_node_id === data.step_id_ref
              ) {
                let targetChildren;
                if (data.substep_id_ref) {
                  if (step.substeps) {
                    const substep = step.substeps.find(
                      (ss) =>
                        ss.substep_id_ref === data.substep_id_ref ||
                        ss.tree_node_id === data.substep_id_ref
                    );
                    if (substep) {
                      targetChildren = substep.children;
                    }
                  }
                } else {
                  targetChildren = step.children;
                }

                if (targetChildren) {
                  const nodeIndex = targetChildren.findIndex(
                    (child) =>
                      child.tree_node_id === id ||
                      child.detail?.canvas_tree_node_id === id
                  );

                  if (nodeIndex !== -1) {
                    const existingNode = targetChildren[nodeIndex];
                    tree_node_id = existingNode.tree_node_id;
                    node_sibling_order = existingNode.sibling_order;
                    existingNode.detail = {
                      ...existingNode.detail,
                      ...data,
                      ctm_data: data.pages.map((page)=>{
                        return {
                          canva_snap_url: page?.canva_snap_url || "",
                          canvas_tree_node_id_ref: `cnv_ref_${Date.now()}`,
                        }
                      }),
                    };
                  }
                }
              }
            });
          }
        }
      });


      const previous_content_snapshot = await uploadJsonToS3(
        allNodesData,
        partnumber,
        revision + "_canvas_previous",
        true
      );

      const revAndTrainingData = await getRevAndTraining(dispatch, data.part_required_id_ref)

      const new_content_snapshot = await uploadJsonToS3({
        data:snapshotPayload,
        ...data.audit_trail_data.version_content,
        tree_node_id,
        operation: "single_audit_trail",
        ...revAndTrainingData,
      },partnumber,revision+"_canvas_new",true)

      const payload = {
        ...data,
        sibling_order: node_sibling_order,
        audit_trail_data: {
          ...data.audit_trail_data,
          previous_content_snapshot,
          new_content_snapshot,
          version_content:{
            action_to_show:data.audit_trail_data.version_content.action_to_show,
          }
        },
      };

      const response = await axios.put(`${canvasBaseUrl}/update/${id}`, payload);
      
      await dispatch(
        updateCurrentSnapshot({
          partId: data.part_required_id_ref,
          revisionId: data.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error creating rich text"
      );
    }
  }
);

// Create Substep
export const createSubstep = createAsyncThunk(
  "step/substepCreate",
  async ({ data, comment, version_content, allNodesData, partnumber, revision }, { dispatch, rejectWithValue }) => {
    const tree_node_id = uuidv4();

    const ad = await getAuditTrailData({
      action: "CREATE_SUBSTEP",
      comment: comment,
      version_content: {
        ...version_content,
        tree_node_id,
      },
    });

    const { item_code_type, status } = JSON.parse(JSON.stringify(version_content));

    const newSubstep = {
      tree_node_id: tree_node_id,
      step_id_ref: data.step_id_ref,
      section_id_ref: data.section_id_ref,
      sibling_order: data.sibling_order,
      procedure_tree: { sibling_order: data.sibling_order },
      created_at: new Date().toISOString(),
      is_deleted: false,
      detail: {
        ...data,
      },
      children: [],
      isDraft: true,
    };

    const snapshotPayload = JSON.parse(JSON.stringify(allNodesData));

    snapshotPayload.forEach((section) => {
      if (section.section_id_ref === data.section_id_ref) {
        if (section.steps && Array.isArray(section.steps)) {
          section.steps.forEach((step) => {
            if (step.step_id_ref === data.step_id_ref) {
              if (!step.substeps) step.substeps = [];
              
              adjustSiblingOrderAndInsert(step.substeps, newSubstep);
            }
          });
        }
      }
    });

    const previous_content_snapshot = await uploadJsonToS3(allNodesData, partnumber, revision + "_substep_previous", true);

    const revAndTrainingData = await getRevAndTraining(dispatch, data.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3({
      data: snapshotPayload,
      ...ad.version_content,
      operation: "single_audit_trail",
      ...revAndTrainingData,
    }, partnumber, revision + "_substep_new", true);

    const payload = {
      ...data,
      audit_trail_data: {
        ...ad,
        previous_content_snapshot,
        new_content_snapshot,
        version_content: {
          action_to_show: ad.version_content.action_to_show,
        }
      },
    };

    try {
      const response = await axios.post(`${substepBaseUrl}/create`, payload);
      
      await dispatch(
        updateCurrentSnapshot({
          partId: data.part_required_id_ref,
          revisionId: data.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;

    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating substep");
    }
  }
);

export const updateSubstep = createAsyncThunk(
  "step/substepUpdate",
  async ({ data, id, allNodesData, partnumber, revision, tree_node_id }, { dispatch, rejectWithValue }) => {

    const { item_code_type, status } = JSON.parse(JSON.stringify(data.audit_trail_data.version_content));

    // Create a deep copy of allNodesData to modify for current/new snapshot
    const snapshotPayload = JSON.parse(JSON.stringify(allNodesData));
    // Update the specific substep in the snapshot payload
    snapshotPayload.forEach((section) => {
      if (section.steps && Array.isArray(section.steps)) {
        section.steps.forEach((step) => {
          if (step.substeps && Array.isArray(step.substeps)) {
            step.substeps.forEach((substep, index) => {
              if (substep.substep_id_ref === id) {
                step.substeps[index] = {
                  test:"test",
                  ...substep,
                  updated_at: new Date().toISOString(),
                  detail: {
                    ...substep.detail,
                    short_description: data.short_description,
                  },

                };
              }
            });
          }
        });
      }
    });

    const previous_content_snapshot = await uploadJsonToS3(allNodesData, partnumber, revision + "_substep_previous", true);

    const revAndTrainingData = await getRevAndTraining(dispatch, data.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3({
      data: snapshotPayload,
      ...data.audit_trail_data.version_content,
      tree_node_id,
      operation: "single_audit_trail",
      ...revAndTrainingData,
    }, partnumber, revision + "_substep_new", true);

    const payload = {
      ...data,
      audit_trail_data: {
        ...data.audit_trail_data,
        previous_content_snapshot,
        new_content_snapshot,
        version_content: {
          action_to_show: data.audit_trail_data.version_content.action_to_show,
        }
      },
    };

    try {
      const response = await axios.put(`${substepBaseUrl}/update/${id}`, payload);
      
      await dispatch(
        updateCurrentSnapshot({
          partId: data.part_required_id_ref,
          revisionId: data.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;

    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating substep");
    }
  }
);

export const moveSubstep = createAsyncThunk(
  "step/substepMove",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}node-cmd/move`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error moving substep");
    }
  }
);

// 📌 Move Step (POST)
export const moveStep = createAsyncThunk(
  "step/move",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}node-cmd/move`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error moving step");
    }
  }
);

/* ──────────────── GET AsyncThunks ──────────────── */

// Get Steps
export const getAllSteps = createAsyncThunk(
  "step/getAll",
  async ({ limit = 10, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching steps");
    }
  }
);

// Get Steps by Section
export const getAllStepsForSection = createAsyncThunk(
  "step/getAllForSection",
  async ({ partId, revisionId, sectionId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?part_ref=${partId}&procedure_rev_ref=${revisionId}&section_id_ref=${sectionId}`
      );
      return { steps: response.data.results, sectionId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching steps for section"
      );
    }
  }
);

// Get All Data of Steps
export const getAllNodes = createAsyncThunk(
  "step/getAllNodes",
  async (
    { partId, revisionId, sectionId, stepId, substepId },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get(
        `${nodeBaseUrl}procedure-revision/get-procedure-nodes?part_ref=${partId}&procedure_rev_ref=${revisionId}&section_id_ref=${sectionId}&step_id_ref=${stepId}&substep_id_ref=${
          substepId === "null" ? "" : substepId
        }`
      );
      return { results: response.data.results, sectionId, stepId, substepId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching steps for section"
      );
    }
  }
);

// Get Substeps by Section & Step
export const getAllSubstepsForStep = createAsyncThunk(
  "step/getAllSubstepsForStep",
  async ({ partId, revisionId, sectionId, stepId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${substepBaseUrl}/get-all?part_ref=${partId}&procedure_rev_ref=${revisionId}&section_id_ref=${sectionId}&step_id_ref=${stepId}`
      );
      return { substeps: response.data.results, stepId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching steps for section"
      );
    }
  }
);

// Generic fetch function for step/substep content
const fetchContent = (type, isSubstep = false) =>
  createAsyncThunk(
    `step/getAll${type}${isSubstep ? "ForSubstep" : "ForStep"}`,
    async (params, { rejectWithValue }) => {
      const {
        partId,
        revisionId,
        sectionId,
        stepId,
        substepId, // optional
      } = params;

      let url = `${
        type === "Attachment"
          ? attachmentBaseUrl
          : type === "Form"
          ? formBaseUrl
          : type === "Video"
          ? videoBaseUrl
          : type === "Canvas"
          ? canvasBaseUrl
          : richTextBaseUrl
      }/${
        type === "Form" ||
        type === "Attachment" ||
        type === "Video" ||
        type === "Canvas"
          ? "get-all"
          : "get-all-filter"
      }`;
      let query = `part_ref=${partId}&procedure_rev_ref=${revisionId}&section_id_ref=${sectionId}&step_id_ref=${stepId}`;
      if (!isSubstep) query += `&substep_id_ref=`;
      if (isSubstep)
        query += `&substep_id_ref=${substepId === "null" ? "" : substepId}`;

      const maxRetries = 3;
      let retryCount = 0;
      let lastError = null;

      while (retryCount < maxRetries) {
        try {
          const response = await axios.get(`${url}?${query}`, {
            timeout: 10000, // 10 second timeout
          });
          return {
            data: response.data.results,
            id: isSubstep ? (substepId === "null" ? "" : substepId) : stepId,
          };
        } catch (error) {
          lastError = error;
          if (
            error.code === "ECONNABORTED" ||
            error.message.includes("timeout")
          ) {
            retryCount++;
            if (retryCount < maxRetries) {
              // Exponential backoff: wait 1s, 2s, 4s between retries
              await new Promise((resolve) =>
                setTimeout(resolve, Math.pow(2, retryCount) * 1000)
              );
              continue;
            }
          }
          // For non-timeout errors or after all retries failed
          return rejectWithValue({
            error:
              error.response?.data || `Error fetching ${type.toLowerCase()}s`,
            message: error.message,
            code: error.code,
            retries: retryCount,
          });
        }
      }

      // If we get here, all retries failed
      return rejectWithValue({
        error:
          lastError.response?.data ||
          `Failed to fetch ${type.toLowerCase()}s after ${maxRetries} retries`,
        message: lastError.message,
        code: lastError.code,
        retries: retryCount,
      });
    }
  );

export const getAllRichTextsForStep = fetchContent("RichText");
export const getAllRichTextsForSubstep = fetchContent("RichText", true);
export const getAllAttachmentsForStep = fetchContent("Attachment");
export const getAllAttachmentsForSubstep = fetchContent("Attachment", true);
export const getAllVideosForStep = fetchContent("Video");
export const getAllVideosForSubstep = fetchContent("Video", true);
export const getAllFormsForStep = fetchContent("Form");
export const getAllFormsForSubstep = fetchContent("Form", true);
export const getAllCanvasesForStep = fetchContent("Canvas");
export const getAllCanvasesForSubstep = fetchContent("Canvas", true);

/* ──────────────── Step Slice ──────────────── */

const stepSlice = createSlice({
  name: "step",
  initialState: {
    steps: [],
    stepsBySection: {},
    substepsByStep: {},
    richTexts: {},
    attachments: {},
    forms: {},
    videos: {},
    canvases: {},
    loading: false,
    error: null,
  },
  reducers: {
    resetSteps: (state) => {
      state.steps = [];
      state.stepsBySection = {};
      state.substepsByStep = {};
      state.richTexts = {};
      state.attachments = {};
      state.forms = {};
      state.videos = {};
      state.canvases = {};
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createStep.pending, (state) => {
        state.loading = true;
      })
      .addCase(createStep.fulfilled, (state, action) => {
        state.loading = false;
        state.steps.push(action.payload);
      })
      .addCase(createStep.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.error || action.payload.message || "An error occurred";
      })

      .addCase(updateStep.fulfilled, (state, action) => {
        state.loading = false;
        state.steps = state.steps.map((step) =>
          step.id === action.payload.id ? action.payload : step
        );
      })

      .addCase(deleteStep.fulfilled, (state, action) => {
        state.loading = false;
        state.steps = state.steps.filter((step) => step.id !== action.payload);
      })

      .addCase(getAllNodes.fulfilled, (state, action) => {
        const results = action.payload.results;
        const stepId = action.payload.stepId;
        const substepId = action.payload.substepId;
        state.loading = false;

        const key = substepId
          ? `${substepId === "null" ? "" : substepId}`
          : `${stepId}`;

        // ✅ Always clear before filling
        state.richTexts[key] = [];
        state.attachments[key] = [];
        state.videos[key] = [];
        state.forms[key] = [];
        state.canvases[key] = [];

        results.forEach((node) => {
          const { type, detail, sibling_order, sub_step_tree_node_id } = node;

          if (!substepId && sub_step_tree_node_id) return;

          const payload = { ...detail, sibling_order, sub_step_tree_node_id };

          switch (type) {
            case "rich-text":
              state.richTexts[key].push(payload);
              break;
            case "attachment":
              state.attachments[key].push(payload);
              break;
            case "video":
              state.videos[key].push(payload);
              break;
            case "form":
              state.forms[key].push(payload);
              break;
            case "canva":
              state.canvases[key].push(payload);
              break;
            default:
              break;
          }
        });
      })

      .addCase(getAllSteps.fulfilled, (state, action) => {
        state.loading = false;
        state.steps = action.payload.results;
      })

      .addCase(getAllStepsForSection.fulfilled, (state, action) => {
        const { sectionId, steps } = action.payload;
        state.loading = false;
        state.stepsBySection[sectionId] = steps;
      })

      .addCase(getAllSubstepsForStep.fulfilled, (state, action) => {
        const { stepId, substeps } = action.payload;
        state.loading = false;
        state.substepsByStep[stepId] = substeps;
      })

      // Generic content reducers
      .addCase(getAllRichTextsForStep.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.richTexts[id] = data || [];
      })
      .addCase(getAllRichTextsForSubstep.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.richTexts[id] = data || [];
      })
      .addCase(getAllAttachmentsForStep.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.attachments[id] = data;
      })
      .addCase(getAllAttachmentsForSubstep.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.attachments[id] = data;
      })
      .addCase(getAllVideosForStep.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.videos[id] = data;
      })
      .addCase(getAllVideosForSubstep.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.videos[id] = data;
      })
      .addCase(getAllFormsForStep.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.forms[id] = data;
      })
      .addCase(getAllFormsForSubstep.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.forms[id] = data;
      })
      .addCase(getAllCanvasesForStep.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.canvases[id] = data;
      })
      .addCase(getAllCanvasesForSubstep.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        state.canvases[id] = data;
      });
  },
});

export const { resetSteps } = stepSlice.actions;
export default stepSlice.reducer;
